/**
 * Eigene Seiten (S50): finished pages of the house (a title page from the designer, its story, a page
 * with its links) as PDF, kept in a small collection per business. Each is a Vorspann (before the
 * menu) or a Nachsatz (after it); a menu names which of them come with it (`ownPages`). They go into
 * print and PDF unchanged and into Word as pictures, never on the net, never with an insert (S37).
 */
import { isId } from "./ids.ts";

export type OwnPageKind = "front" | "back";

export interface OwnPage {
  id: string;
  name: string;
  kind: OwnPageKind;
  /** The file in the business's store (files.ts on the server): the hash of its content and „.pdf“. */
  file: string;
  pages: number;
  /** The size of its first page, in points. */
  width: number;
  height: number;
  bytes: number;
  createdAt: number;
}

/** What a menu takes of them: ids in their order, before and after it. */
export interface MenuOwnPages {
  front: string[];
  back: string[];
}

export const OWN_PAGE_LIMITS = {
  /** A file at most this large (S50: 20 MB, as Caddy lets it through for this path). */
  bytes: 20_000_000,
  pages: 8,
  /** So many per business, together at most `total` (S50, Vorschlag). */
  perBusiness: 10,
  total: 100_000_000,
  name: 80,
  /** So many of them before and after one menu. */
  perMenu: 6,
} as const;

export const OWN_PAGE_KINDS: Record<OwnPageKind, string> = { front: "Vorspann", back: "Nachsatz" };

/** A menu's choice as it may be kept: ids, each once, at most `perMenu` a side; null when it takes none. */
export function cleanOwnPages(raw: unknown): MenuOwnPages | null {
  const o = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const ids = (v: unknown) => [...new Set((Array.isArray(v) ? v : []).filter(isId))].slice(0, OWN_PAGE_LIMITS.perMenu);
  const front = ids(o.front);
  const back = ids(o.back);
  return front.length || back.length ? { front, back } : null;
}

/** The pages a menu takes that are still in the collection, in the menu's order. */
export function ownPagesOf(menu: { ownPages?: MenuOwnPages }, all: readonly OwnPage[]): { front: OwnPage[]; back: OwnPage[] } {
  const pick = (ids: readonly string[] = []) => ids.flatMap((id) => all.filter((p) => p.id === id));
  return { front: pick(menu.ownPages?.front), back: pick(menu.ownPages?.back) };
}

/** Whether a page has another size than the paper of the menu (more than 2 mm off): it is fitted then, with a hint. */
export const otherSize = (page: Pick<OwnPage, "width" | "height">, paper: { width: number; height: number }) =>
  Math.abs(page.width - paper.width) > 6 || Math.abs(page.height - paper.height) > 6;

/**
 * What reading a PDF may keep (S50): the pages without dishes at its start as a Vorspann, those at its
 * end as a Nachsatz, each run unbroken from the first or to the last page. Pages in between are left
 * out only.
 */
export function keepableOf(skipped: readonly number[], pages: number): { front: number[]; back: number[] } {
  const set = new Set(skipped.filter((n) => n >= 1 && n <= pages));
  const front: number[] = [];
  for (let n = 1; set.has(n) && n <= pages; n++) front.push(n);
  const back: number[] = [];
  for (let n = pages; set.has(n) && n > (front.at(-1) ?? 0); n--) back.unshift(n);
  return { front, back };
}

/** „Seite 1–2“, „Seite 8“, „Seiten 1, 3“ for the offer. */
export function pagesText(pages: readonly number[]): string {
  if (!pages.length) return "";
  const run = pages.every((n, k) => k === 0 || n === pages[k - 1]! + 1);
  return pages.length === 1 ? `Seite ${pages[0]}` : run ? `Seite ${pages[0]}–${pages.at(-1)}` : `Seiten ${pages.join(", ")}`;
}
